/**
 * "Ask Claude" panel on the ticket detail page.
 *
 * The question is answered against the ticket's own recorded context — its
 * verify command, decision and rejected notes, its blocks graph and its
 * subsystem's traps — so the answer is grounded in what was already tried.
 *
 * Effort defaults to `low` because most ticket questions are lookups over
 * supplied context. The selector exists for the questions that deserve more.
 */

import { useState } from 'react'
import { api } from '../api.js'

/** Fable 5 effort levels, with what each is for and roughly what it costs. */
const EFFORTS = [
  { value: 'low', label: 'low', hint: 'default — lookups over the given context' },
  { value: 'medium', label: 'medium', hint: 'some reasoning required' },
  { value: 'high', label: 'high', hint: 'genuinely hard question' },
  { value: 'xhigh', label: 'xhigh', hint: 'deep analysis' },
  { value: 'max', label: 'max', hint: 'correctness over cost' },
]

export default function AskClaude({ ticketKey, onAnswered }) {
  const [question, setQuestion] = useState('')
  const [effort, setEffort] = useState('low')
  const [save, setSave] = useState(true)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function submit(event) {
    event.preventDefault()
    if (!question.trim() || busy) return
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const answer = await api.askTicket(ticketKey, question.trim(), effort, save)
      setResult(answer)
      // A saved answer becomes a comment, so the thread needs reloading.
      if (answer.comment_id && onAnswered) onAnswered()
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="ask-claude">
      <h2>Ask Claude</h2>
      <p className="ask-hint">
        Answered against this ticket&apos;s verify command, decision and rejected
        notes, its blockers, and the recorded traps for its components.
      </p>

      <form onSubmit={submit}>
        <textarea
          rows={3}
          value={question}
          placeholder="What's actually blocking this? Is this approach sound?"
          onChange={(e) => setQuestion(e.target.value)}
          disabled={busy}
        />
        <div className="ask-controls">
          <label>
            effort{' '}
            <select value={effort} onChange={(e) => setEffort(e.target.value)} disabled={busy}>
              {EFFORTS.map((e) => (
                <option key={e.value} value={e.value} title={e.hint}>
                  {e.label}
                </option>
              ))}
            </select>
          </label>
          <label className="ask-save">
            <input
              type="checkbox"
              checked={save}
              onChange={(e) => setSave(e.target.checked)}
              disabled={busy}
            />{' '}
            save to ticket
          </label>
          <button type="submit" disabled={busy || !question.trim()}>
            {busy ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </form>

      {error && <div className="ask-error">{error}</div>}

      {result && (
        <div className={`ask-answer${result.refused ? ' ask-refused' : ''}`}>
          <div className="ask-answer-body">{result.answer}</div>
          <div className="ask-answer-meta">
            {result.model} · effort {result.effort} ·{' '}
            {result.input_tokens.toLocaleString()} in /{' '}
            {result.output_tokens.toLocaleString()} out · ${result.cost_usd.toFixed(4)}
            {result.fallback_model && ` · served by ${result.fallback_model}`}
            {result.comment_id && ' · saved to this ticket'}
          </div>
        </div>
      )}
    </section>
  )
}
